import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IMatrixChartResponse,
  IMatrixTooltip,
} from "src/Interfaces/chartInterfaces";
import { toast } from "react-toastify";

interface Props {
  data: IMatrixChartResponse;
}

const COLORS = [
  "rgb(0, 0, 128)",
  "rgb(30, 60, 150)",
  "rgb(70, 130, 180)",
  "rgb(100, 149, 237)",
  "rgb(0, 0, 205)",
  "rgb(70, 168, 255)",
  "rgb(30, 144, 255)",
  "rgb(0, 191, 255)",
  "rgb(135, 206, 235)",
  "rgb(176, 224, 230)",
];

const PAD = { top: 24, right: 24, bottom: 48, left: 56 };

type Cell = {
  xmin: number;
  xmax: number;
  ymin: number;
  ymax: number;
  color: string;
  tooltips: IMatrixTooltip[];
};

/**
 * Linear X/Y matrix (TE% × DEI). Lightweight Charts has no linear X for arbitrary
 * floats, so this is drawn on canvas — same visual as the old Chart.js matrix.
 */
const MatrixChart: React.FC<Props> = ({ data }) => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [hovered, setHovered] = useState<{
    x: number;
    y: number;
    tooltips: IMatrixTooltip[];
  } | null>(null);
  const [size, setSize] = useState({ w: 0, h: 600 });

  useEffect(() => {
    if (data?.comingsoon) {
      toast.warning("درحال محاسبه مجدد!", { theme: "colored" });
    }
  }, [data]);

  const cells: Cell[] = useMemo(() => {
    const colCount = data.colCount || 8;
    return (
      data.squares?.map((sq, i) => ({
        xmin: sq.xmin,
        xmax: sq.xmax,
        ymin: sq.ymin,
        ymax: sq.ymax,
        color: COLORS[i % colCount],
        tooltips: sq.tooltips || [],
      })) || []
    );
  }, [data]);

  const bounds = useMemo(() => {
    if (!cells.length) {
      return { minX: 0, maxX: 1, minY: 0, maxY: 1 };
    }
    const xs = cells.flatMap((c) => [c.xmin, c.xmax]);
    const ys = cells.flatMap((c) => [c.ymin, c.ymax]);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const padX = Math.max((maxX - minX) * 0.04, 0.01);
    const padY = Math.max((maxY - minY) * 0.04, 0.05);
    return {
      minX: minX - padX,
      maxX: maxX + padX,
      minY: Math.max(0, minY - padY),
      maxY: maxY + padY,
    };
  }, [cells]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const w = Math.max(1, Math.floor(entry.contentRect.width));
      const h = Math.max(1, Math.floor(entry.contentRect.height));
      setSize({ w, h });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const mapX = (v: number, plotW: number) =>
    PAD.left +
    ((v - bounds.minX) / Math.max(bounds.maxX - bounds.minX, 1e-9)) * plotW;

  const mapY = (v: number, plotH: number) =>
    PAD.top +
    (1 - (v - bounds.minY) / Math.max(bounds.maxY - bounds.minY, 1e-9)) *
      plotH;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.w <= 0 || size.h <= 0) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.floor(size.w * dpr);
    canvas.height = Math.floor(size.h * dpr);
    canvas.style.width = `${size.w}px`;
    canvas.style.height = `${size.h}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.w, size.h);

    const plotW = size.w - PAD.left - PAD.right;
    const plotH = size.h - PAD.top - PAD.bottom;

    // background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size.w, size.h);

    // grid
    ctx.strokeStyle = "#e5e7eb";
    ctx.lineWidth = 1;
    const xTicks = niceTicks(bounds.minX, bounds.maxX, 8);
    const yTicks = niceTicks(bounds.minY, bounds.maxY, 8);
    xTicks.forEach((t) => {
      const x = mapX(t, plotW);
      ctx.beginPath();
      ctx.moveTo(x, PAD.top);
      ctx.lineTo(x, PAD.top + plotH);
      ctx.stroke();
    });
    yTicks.forEach((t) => {
      const y = mapY(t, plotH);
      ctx.beginPath();
      ctx.moveTo(PAD.left, y);
      ctx.lineTo(PAD.left + plotW, y);
      ctx.stroke();
    });

    // cells
    cells.forEach((cell) => {
      const x1 = mapX(cell.xmin, plotW);
      const x2 = mapX(cell.xmax, plotW);
      const y1 = mapY(cell.ymin, plotH);
      const y2 = mapY(cell.ymax, plotH);
      const left = Math.min(x1, x2);
      const top = Math.min(y1, y2);
      const w = Math.max(Math.abs(x2 - x1), 1);
      const h = Math.max(Math.abs(y2 - y1), 1);
      ctx.fillStyle = cell.color;
      ctx.strokeStyle = "rgba(0,0,0,0.3)";
      ctx.lineWidth = 0.5;
      ctx.fillRect(left, top, w, h);
      ctx.strokeRect(left, top, w, h);
    });

    // axes frame
    ctx.strokeStyle = "#9ca3af";
    ctx.lineWidth = 1;
    ctx.strokeRect(PAD.left, PAD.top, plotW, plotH);

    // tick labels
    ctx.fillStyle = "#374151";
    ctx.font = "11px Tahoma, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    xTicks.forEach((t) => {
      ctx.fillText(formatTick(t), mapX(t, plotW), PAD.top + plotH + 8);
    });
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    yTicks.forEach((t) => {
      ctx.fillText(formatTick(t), PAD.left - 8, mapY(t, plotH));
    });

    // axis titles
    ctx.fillStyle = "#111827";
    ctx.font = "12px Tahoma, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";
    if (data.labelx) {
      ctx.fillText(data.labelx, PAD.left + plotW / 2, size.h - 6);
    }
    if (data.labely) {
      ctx.save();
      ctx.translate(14, PAD.top + plotH / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(data.labely, 0, 0);
      ctx.restore();
    }
  }, [cells, bounds, size, data.labelx, data.labely]);

  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || !cells.length) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const plotW = size.w - PAD.left - PAD.right;
    const plotH = size.h - PAD.top - PAD.bottom;

    const found = cells.find((cell) => {
      const x1 = mapX(cell.xmin, plotW);
      const x2 = mapX(cell.xmax, plotW);
      const y1 = mapY(cell.ymin, plotH);
      const y2 = mapY(cell.ymax, plotH);
      const left = Math.min(x1, x2);
      const right = Math.max(x1, x2);
      const top = Math.min(y1, y2);
      const bottom = Math.max(y1, y2);
      return mx >= left && mx <= right && my >= top && my <= bottom;
    });

    if (found) {
      setHovered({ x: mx, y: my, tooltips: found.tooltips });
    } else {
      setHovered(null);
    }
  };

  return (
    <div className="flex flex-row justify-start items-start gap-10 w-full">
      <div className="grid grid-cols-2 gap-1 whitespace-nowrap shrink-0">
        {(data.comments || []).map((comment) => (
          <React.Fragment key={`${comment.name}-${comment.value}`}>
            <div>{comment.name}:</div>
            <div className="font-semibold">{comment.value}</div>
          </React.Fragment>
        ))}
      </div>
      <div
        ref={wrapRef}
        className="relative w-full min-w-[600px]"
        style={{ height: 600, direction: "ltr" }}
        dir="ltr"
      >
        <canvas
          ref={canvasRef}
          className="absolute inset-0"
          onMouseMove={onMouseMove}
          onMouseLeave={() => setHovered(null)}
        />
        {hovered ? (
          <div
            className="pointer-events-none absolute z-20 rounded border border-gray-200 bg-white px-2 py-1 text-xs shadow text-left"
            dir="ltr"
            style={{ left: hovered.x + 12, top: hovered.y + 12 }}
          >
            {hovered.tooltips.map((t) => (
              <div key={`${t.name}-${t.value}`}>
                {t.name}: {t.value}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

function formatTick(v: number): string {
  if (Math.abs(v) >= 10) return v.toFixed(0);
  if (Math.abs(v) >= 1) return v.toFixed(2);
  return v.toFixed(2);
}

function niceTicks(min: number, max: number, count: number): number[] {
  const span = Math.max(max - min, 1e-9);
  const raw = span / Math.max(count - 1, 1);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  let step = mag;
  if (norm >= 5) step = 5 * mag;
  else if (norm >= 2) step = 2 * mag;
  else step = mag;

  const start = Math.ceil(min / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= max + step * 1e-9; v += step) {
    ticks.push(Number(v.toPrecision(12)));
  }
  return ticks.length ? ticks : [min, max];
}

export default MatrixChart;
